import TextButton from '@oberoninternal/travelbase-ds/components/action/TextButton';
import Img from '../Img';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import { Maybe } from '@oberoninternal/travelbase-ds/entities/Maybe';
import useSesame from '@oberoninternal/travelbase-ds/hooks/useSesame';
import { Box, Flex } from '@rebass/grid';
import { gql } from '@apollo/client';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import React, { FC, useRef, useState } from 'react';
import { FormattedList, FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import messages from '../../constants/messages';
import { getActivitySeoMessages } from '../../constants/seoMessages';
import { addApolloState, initApolloClient } from '../../createApolloClient';
import { Locale } from '../../entities/Locale';
import NotFoundError from '../../entities/NotFoundError';

import {
    ActivityBookingTypesEnum,
    ActivityDetailDocument,
    ActivityDetailQuery,
    ActivityDetailQueryVariables,
    ActivityPlannerTimeslotFragment,
    CartStatusEnum,
    GenericImage,
    useActivityDetailQuery,
    useActivityPlannerQuery,
} from '../../generated/graphql';
import { getTicketInput, useCart } from '../../hooks/useCart';
import useDateParam from '../../hooks/useDateParam';
import { getCartId } from '../../utils/cart';
import createImgProps from '../../utils/createImgProps';
import createTicketText from '../../utils/createTicketText';
import getTargetAudienceMessage from '../../utils/getTargetAudienceMessage';
import parseDateTime from '../../utils/parseDateTime';
import setCanonicalLink from '../../utils/setCanonicalLink';
import ActivityAlternatives from '../ActivityAlternatives';
import ActivityCheckoutModal from '../ActivityCheckoutModal';
import ActivityEateries from '../ActivityEateries';
import ActivityPlanner, { OnSelectScrollOptions } from '../ActivityPlanner';
import ActivityPlannerModal from '../ActivityPlannerModal';
import ContentWrapper from '../ContentWrapper';
import Cell from '../designsystem/Cell';
import GradientBackground from '../designsystem/GradientBackground';
import Grid from '../designsystem/Grid';
import Heading from '../designsystem/Heading';
import Hero, { HeroButtonsDesktop, HeroButtonsMobile, HeroContainer, HeroPhotosButton } from '../Hero';
import Nav from '../designsystem/Nav';
import Section from '../designsystem/Section';
import TextClamp from '../designsystem/TextClamp';
import { SectionDivider } from '../Divider';
import Intro from '../Intro';
import LoadingDetail from '../LoadingDetail';
import LocationMap, { devMarkerUrl, devMarkerUrl2x } from '../LocationMap';
import Page from '../Page';
import PhotoSection from '../PhotoSection';
import StickyBar, { StickyBarThumnail } from '../StickyBar';
import Capacity from '../svg/Capacity';
import Flag from '../svg/Flag';
import LinkIcon from '../svg/Link';
import Location from '../svg/Location';
import TargetAudience from '../svg/TargetAudience';
import TwoColumnIntro, { PracticalList } from '../TwoColumnIntro';
import Play from '@oberoninternal/travelbase-ds/components/figure/Play';
import MediaButton from '../designsystem/MediaButton';
import VideoModal from '../VideoModal';
import ActivityTickets from '../ActivityTickets';
import { useUpdateCart } from '../../hooks/useUpdateCart';
import Head from 'next/head';
import { useTenantContext } from '../../context/TenantContext';
import FavoriteButton from '../FavoriteButton';
import startOfMonth from 'date-fns/startOfMonth';
import { endOfMonth } from 'date-fns';
import parseToDateString from '../../constants/parseToDateString';
import useAnalyticsActivityView from '../../hooks/analytics/useAnalyticsActivityView';
import Link from 'next/link';
import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import useGetPageSlugByLocale from '../../hooks/useGetPageSlugByLocale';
import ActivityBookButton from '../ActivityStickyBar';
import Reviews from '../Reviews';

export const pageQuery = gql`
    query ActivityDetail($slug: String!) {
        activity(slug: $slug) {
            id
            slug
            externalBookingUrl
            externalInfoUrl
            bookingType
            videoUrl
            jpegHero: mainImage {
                transform(config: HERO, format: JPEG) {
                    ...Transform
                }
            }
            webpHero: mainImage {
                transform(config: HERO, format: WEBP) {
                    ...Transform
                }
            }
            jpegThumbnail: listImage {
                transform(config: TILE, format: JPEG) {
                    ...Transform
                }
            }
            webpThumbnail: listImage {
                transform(config: TILE, format: WEBP) {
                    ...Transform
                }
            }
            place
            name
            minCapacity
            maxCapacity
            targetAudiences
            themes {
                id
                name
            }
            activityReviewStats {
                count
                average
                activity
                hospitality
                valueForMoney
            }

            activityReviews(limit: 3, offset: 0) {
                ...ActivityReviews
            }
            description

            # location section
            coordinates {
                lat
                lon
            }
            descriptionLocation

            # particulars section
            descriptionParticulars

            # images section
            images {
                jpegTile: transform(config: TILE, format: JPEG) {
                    ...Transform
                }
                webpTile: transform(config: TILE, format: WEBP) {
                    ...Transform
                }
                jpegGallery: transform(config: GALLERY, format: JPEG) {
                    ...Transform
                }
                webpGallery: transform(config: GALLERY, format: WEBP) {
                    ...Transform
                }
            }

            # company info section

            company {
                id
                name
                description
                slug
                webpMain: mainImage {
                    transform(config: HERO, format: WEBP) {
                        ...Transform
                    }
                }
                jpegMain: mainImage {
                    transform(config: HERO, format: JPEG) {
                        ...Transform
                    }
                }
                ...CompanyOperatingHours
            }
        }
    }
`;

const isNumber = (...vals: Maybe<number>[]) => vals.every(val => typeof val === 'number');

const ActivityDetail: FC<React.PropsWithChildren<unknown>> = () => {
    const router = useRouter();
    const slug = router.query.slug as string;
    const { onOpen, onClose, open } = useSesame();
    const ticketModal = useSesame();
    const { getPageSlugByLocale } = useGetPageSlugByLocale();
    const companyPageSlug = getPageSlugByLocale('COMPANY');
    const [activityTickets, setActivityTickets] = useState<boolean>(false);

    const [openActivityTickets, setOpenActivityTickets] = useState<boolean>(false);
    const [openActivityPlanner, setOpenActivityPlanner] = useState<boolean>(false);

    const intl = useIntl();
    const cart = useCart();

    const currentTickets =
        cart.cartQuery.data?.cart?.status === CartStatusEnum.Open
            ? cart.tickets.filter(item => item.activity?.slug === slug)
            : [];

    const [timeslot, setSelectedTimeslot] = useState<ActivityPlannerTimeslotFragment | null>(null);
    const currentTicket = currentTickets.find(ticket => ticket.timeslot?.id === timeslot?.id);
    const onSetTimeslot = (slot: ActivityPlannerTimeslotFragment | null) => {
        setSelectedTimeslot(slot);
        setActivityTickets(true);
        setOpenActivityTickets(true);
    };
    const plannerModal = useSesame();
    const [selectedDay, setSelectedDay] = useDateParam();
    const [selectedDayIsBookable, setSelectedDayIsBookable] = useState(false);
    const { open: videoModalIsOpen, onOpen: onOpenVideoModal, onClose: onCloseVideoModal } = useSesame();
    const { brandConfig } = useTenantContext();

    const ticketText =
        currentTickets.length > 0
            ? intl.formatList(
                  currentTickets.map(ticket => {
                      if (!ticket.timeslot?.id) {
                          return null;
                      }
                      return (
                          <TextButton
                              onClick={() => {
                                  plannerModal.onOpen();
                                  setSelectedDay(parseDateTime(ticket.startDateTime));
                              }}
                              size="tiny"
                              key={ticket.timeslot.id}
                          >
                              {createTicketText(intl, ticket.startDateTime, ticket.rateLines)}
                          </TextButton>
                      );
                  })
              )
            : null;

    const { loading, data } = useActivityDetailQuery({
        variables: {
            slug,
        },
    });

    // preload selected / first month before modal opens
    useActivityPlannerQuery({
        variables: {
            startDate: parseToDateString(startOfMonth(selectedDay ?? new Date())),
            endDate: parseToDateString(endOfMonth(selectedDay ?? new Date())),
            slug,
        },
    });
    const modalRef = useRef<HTMLDivElement | null>(null);
    const updateCart = useUpdateCart(timeslot!, slug, data?.activity?.id);
    useAnalyticsActivityView(data?.activity);

    const onEdit = () => {
        setActivityTickets(false);
    };

    if (loading) {
        return <LoadingDetail withHero />;
    }

    if (!data?.activity) {
        throw new NotFoundError();
    }

    const {
        images,
        jpegHero,
        webpHero,
        jpegThumbnail,
        webpThumbnail,
        name,
        activityReviewStats,
        activityReviews,
        minCapacity,
        maxCapacity,
        targetAudiences,
        place,
        externalBookingUrl,
        externalInfoUrl,
        coordinates,
        themes,
        descriptionLocation,
        descriptionParticulars,
        company,
        description,
        videoUrl,
        bookingType,
        id,
    } = data.activity;

    const renderHero = (webp: GenericImage, jpeg: GenericImage, className?: string) => (
        <Hero ratio={9 / 16} {...createImgProps(jpeg, webp)} className={className}>
            {images.length > 0 && (
                <HeroButtonsMobile>
                    <HeroPhotosButton onClick={onOpen}>{images.length}</HeroPhotosButton>
                    {videoUrl && (
                        <MediaButton icon={<Play />} onClick={() => onOpenVideoModal()}>
                            <FormattedMessage defaultMessage="Video" />
                        </MediaButton>
                    )}
                </HeroButtonsMobile>
            )}
            <HeroButtonsDesktop>
                <HeroPhotosButton onClick={onOpen}>{images.length}</HeroPhotosButton>
                {videoUrl && (
                    <MediaButton icon={<Play />} onClick={() => onOpenVideoModal()}>
                        <FormattedMessage defaultMessage="Video bekijken" />
                    </MediaButton>
                )}
            </HeroButtonsDesktop>
        </Hero>
    );
    const { title: seoTitle, description: seoDescription } = getActivitySeoMessages(data.activity, intl);
    const scrollOptions: OnSelectScrollOptions = {
        scrollRef: modalRef,
    };

    const buyTickets = async () => {
        if (!getCartId()) {
            return;
        }
        await router.push(`/checkout/extras`);
    };

    const onActivityBack = () => {
        if (openActivityTickets) {
            setOpenActivityTickets(false);
        } else if (openActivityPlanner && !openActivityTickets) {
            setOpenActivityPlanner(false);
            setOpenActivityTickets(false);
        }
    };

    const onActivityPlanner = () => {
        setOpenActivityPlanner(true);
        setOpenActivityTickets(false);
    };

    const pathname = `/${companyPageSlug}/${company?.slug}`;

    const renderCompanyButton = (className: string) => (
        <Link href={{ pathname }} passHref>
            <Button style={{ display: 'inline-flex' }} as="a" className={className} variant="outline">
                <FormattedMessage defaultMessage="Bekijk {name}" values={{ name: company?.name }} />
            </Button>
        </Link>
    );

    return (
        <>
            <Head>
                <title>{`${seoTitle} - ${brandConfig.seo.siteName}`}</title>
                <meta name="description" content={seoDescription} />
                <meta property="og:title" content={seoTitle} />
                <meta name="og:description" content={seoDescription} />
                {jpegThumbnail?.transform?.src && <meta property="og:image" content={jpegThumbnail.transform.src} />}
                <meta property="og:description" content={seoDescription} />
                <meta property="og:image:alt" content={seoTitle} />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={seoTitle} />
                <meta name="twitter:description" content={seoDescription} />
                {jpegThumbnail?.transform?.src && <meta name="twitter:image" content={jpegThumbnail.transform.src} />}
                <meta name="twitter:image:alt" content={seoTitle} />
            </Head>
            <Page pb="var(--booking-bar-height)">
                {videoUrl && <VideoModal open={videoModalIsOpen} onClose={onCloseVideoModal} src={videoUrl} />}

                <HeroContainer>
                    {webpHero && jpegHero && renderHero(webpHero, jpegHero)}
                    {(!webpHero || !jpegHero) &&
                        webpThumbnail &&
                        jpegThumbnail &&
                        renderHero(webpThumbnail, jpegThumbnail, 'lt-s')}
                </HeroContainer>
                <Section name="intro">
                    <TwoColumnIntro
                        leftChildren={
                            <>
                                <Intro
                                    description={description ?? undefined}
                                    tagline={name}
                                    rating={activityReviewStats?.average}
                                />
                                {brandConfig.enableFavorites && (
                                    <Box mb={[0, null, '64px']} mt={[8, null, 0]}>
                                        <FavoriteButton type="activity" id={id} />
                                    </Box>
                                )}
                            </>
                        }
                        rightChildren={
                            <>
                                <Heading variant="small">
                                    <Title>
                                        <FormattedMessage defaultMessage="Praktisch" />
                                    </Title>
                                </Heading>
                                <PracticalList>
                                    {isNumber(minCapacity, maxCapacity) && (
                                        <li>
                                            <Capacity />
                                            <Body>
                                                {minCapacity !== maxCapacity ? (
                                                    <FormattedMessage
                                                        defaultMessage="{minCapacity} tot {maxCapacity} personen"
                                                        values={{ minCapacity, maxCapacity }}
                                                    />
                                                ) : (
                                                    intl.formatMessage(messages.persons, { amountPersons: minCapacity })
                                                )}
                                            </Body>
                                        </li>
                                    )}
                                    {place && (
                                        <li>
                                            <Location width={24} />
                                            <Body>{place}</Body>
                                        </li>
                                    )}
                                    {themes.length > 0 && (
                                        <li>
                                            <Flag />
                                            <Body>
                                                <FormattedList
                                                    value={themes.map(theme => theme.name)}
                                                    type="conjunction"
                                                />
                                            </Body>
                                        </li>
                                    )}
                                    {targetAudiences && (
                                        <li>
                                            <TargetAudience />
                                            <TargetAudienceBody>
                                                <FormattedList
                                                    value={targetAudiences.map(targetAudience =>
                                                        getTargetAudienceMessage(targetAudience, intl)
                                                    )}
                                                    type="conjunction"
                                                />
                                            </TargetAudienceBody>
                                        </li>
                                    )}
                                    {bookingType === ActivityBookingTypesEnum.NotBookable && externalInfoUrl && (
                                        <li>
                                            <LinkIcon />
                                            <a href={externalInfoUrl} target="_blank" rel="noreferrer noopener">
                                                {new URL(externalInfoUrl).hostname}
                                            </a>
                                        </li>
                                    )}
                                </PracticalList>
                            </>
                        }
                    />
                </Section>
                <SectionDivider />

                <PhotoSection open={open} onClose={onClose} onOpen={onOpen} photos={images} />

                <Section id="location">
                    <GradientBackground />
                    <ContentWrapper>
                        <Grid>
                            <Cell gridColumn={['1 / -1', null, null, '1 / 5', null, '1 / 6']}>
                                <Heading>
                                    <Title variant="large" elementType="h2">
                                        <FormattedMessage defaultMessage="Locatie" />
                                    </Title>
                                </Heading>
                                {descriptionLocation && <TextClamp text={descriptionLocation} />}
                            </Cell>
                            {coordinates && (
                                <Cell
                                    mt={[6, null, null, 0]}
                                    gridColumn={['1 / -1', null, null, '5 / -1', null, '6 / -1']}
                                >
                                    <LocationMap
                                        markerElement="flag"
                                        iconUrl={`${
                                            process.env.NEXT_PUBLIC_BASE_URL
                                                ? `${process.env.NEXT_PUBLIC_BASE_URL}/static/img/flagMarker.png`
                                                : devMarkerUrl
                                        }`}
                                        iconUrl2x={`${
                                            process.env.NEXT_PUBLIC_BASE_URL
                                                ? `${process.env.NEXT_PUBLIC_BASE_URL}/static/img/flagMarker2x.png`
                                                : devMarkerUrl2x
                                        }`}
                                        position={{
                                            longitude: coordinates.lon,
                                            latitude: coordinates.lat,
                                        }}
                                        markerPosition={{
                                            longitude: coordinates.lon,
                                            latitude: coordinates.lat,
                                        }}
                                    />
                                </Cell>
                            )}
                        </Grid>
                    </ContentWrapper>
                </Section>
                <SectionDivider />
                {descriptionParticulars && (
                    <Section id="particulars">
                        <ContentWrapper>
                            <Heading>
                                <Title variant="large" elementType="h2">
                                    <FormattedMessage defaultMessage="Belangrijk om te weten" />
                                </Title>
                            </Heading>
                            <TextClamp style={{ maxWidth: '56.8rem' }} text={descriptionParticulars} linkifyUrls />
                        </ContentWrapper>
                    </Section>
                )}
                <SectionDivider />
                <Section className="gt-s" id="prices">
                    <ActivityContentWrapper>
                        {!activityTickets && (
                            <ActivityPlanner
                                activity={data.activity}
                                setSelectedDayIsBookable={setSelectedDayIsBookable}
                                slug={slug}
                                selectedDay={selectedDay}
                                setSelectedDay={setSelectedDay}
                                setTimeslot={onSetTimeslot}
                            />
                        )}
                        {activityTickets && (
                            <ActivityTickets
                                activity={data.activity}
                                setTicket={updateCart}
                                onClose={ticketModal.onClose}
                                open={ticketModal.open}
                                timeslot={timeslot}
                                ticket={currentTicket ? getTicketInput(currentTicket) : null}
                                setEdit={onEdit}
                            />
                        )}
                    </ActivityContentWrapper>
                </Section>

                <Section id="reviews">
                    <ActivityContentWrapper>
                        <Flex
                            flexDirection={['column', null, 'row', null]}
                            justifyContent="space-around"
                            mr={[0, null, 6]}
                            mb={[6, null, 0]}
                        >
                            {activityReviewStats && activityReviews && (
                                <Box flex={1}>
                                    <Reviews
                                        reviewStats={activityReviewStats}
                                        reviews={activityReviews}
                                        slug={slug as string}
                                        ownerName={company?.name}
                                        ownerImage={jpegHero && webpHero && createImgProps(jpegHero, webpHero)}
                                    />
                                </Box>
                            )}
                            {company && (
                                <Box flex={1}>
                                    {company.jpegMain && company.webpMain && (
                                        <Box flex={1} width="100%">
                                            <Heading className="lt-s">
                                                <Title variant="large" elementType="h2">
                                                    {company?.name}
                                                </Title>
                                            </Heading>
                                            <Box pb={[0, null, 5]}>
                                                <Box pb={[0, null, 6]}>
                                                    <Img
                                                        layout="ratio"
                                                        ratio={9 / 16}
                                                        loading="lazy"
                                                        {...createImgProps(company.jpegMain, company.webpMain)}
                                                    />
                                                </Box>
                                                {renderCompanyButton('gt-m')}
                                            </Box>
                                        </Box>
                                    )}
                                    <Box flex={1}>
                                        <Heading className="gt-s">
                                            <Title variant="large" elementType="h2">
                                                {company?.name}
                                            </Title>
                                        </Heading>
                                        <Box pb={[0, null, 4]}>
                                            {company.description && (
                                                <TextClamp
                                                    alwaysClamp
                                                    style={{ marginTop: '3.2rem' }}
                                                    text={company.description}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Flex>
                    </ActivityContentWrapper>
                </Section>
                {bookingType !== ActivityBookingTypesEnum.Direct && <ActivityAlternatives currentActivitySlug={slug} />}
                <ActivityEateries coordinates={coordinates} />
            </Page>
            <StickyBar>
                {jpegThumbnail && webpThumbnail && (
                    <StickyBarThumnail
                        className="gt-l"
                        title={name}
                        thumbnail={{
                            ...createImgProps(jpegThumbnail, webpThumbnail, undefined, { loading: 'lazy' }),
                        }}
                    />
                )}
                <Nav
                    className="gt-s"
                    items={[
                        {
                            label: <FormattedMessage defaultMessage="Overzicht" />,
                            name: 'intro',
                        },
                        {
                            label: <FormattedMessage defaultMessage="Ligging" />,
                            name: 'location',
                        },
                        ...(descriptionParticulars
                            ? [
                                  {
                                      label: <FormattedMessage defaultMessage="Belangrijk" />,
                                      name: 'particulars',
                                  },
                              ]
                            : []),
                        {
                            label: <FormattedMessage defaultMessage="Prijs" />,
                            name: 'prices',
                            className: 'gt-s',
                        },
                        ...(activityReviews.length > 0
                            ? [
                                  {
                                      label: (
                                          <FormattedMessage
                                              defaultMessage="Beoordelingen ({count})"
                                              values={{ count: activityReviewStats?.count }}
                                          />
                                      ),
                                      name: 'reviews',
                                      className: 'gt-s',
                                  },
                              ]
                            : []),
                    ]}
                />
                <ActivityBookButton
                    bookingType={bookingType}
                    selectedDayIsBookable={selectedDayIsBookable}
                    ticketText={ticketText}
                    externalBookingUrl={externalBookingUrl}
                    onPlannerModalOpen={plannerModal.onOpen}
                    onBuyTickets={buyTickets}
                />
            </StickyBar>

            <ActivityCheckoutModal
                activity={data.activity}
                setTicket={updateCart}
                onClose={ticketModal.onClose}
                open={ticketModal.open}
                timeslot={timeslot}
                ticket={currentTicket ? getTicketInput(currentTicket) : null}
            />

            <ActivityPlannerModal
                activity={data.activity}
                slug={slug}
                selectedDay={selectedDay}
                setSelectedDay={setSelectedDay}
                open={plannerModal.open}
                onClose={plannerModal.onClose}
                setTimeslot={onSetTimeslot}
                onSelectScrollOpts={scrollOptions}
                setSelectedDayIsBookable={setSelectedDayIsBookable}
                setTicket={updateCart}
                ticket={currentTicket ? getTicketInput(currentTicket) : null}
                timeslot={timeslot}
                setEdit={onEdit}
                activityTickets={activityTickets}
                openActivityTickets={openActivityTickets}
                onActvityBack={onActivityBack}
                openActivityPlanner={openActivityPlanner}
                onActivityPlanner={onActivityPlanner}
                onModalClose={plannerModal.onClose}
            />
        </>
    );
};

export default ActivityDetail;

const ActivityContentWrapper = styled(ContentWrapper)`
    padding-bottom: 0px !important;
`;

const TargetAudienceBody = styled(Body)`
    ::first-letter {
        text-transform: capitalize;
    }
`;

export const getServerSideProps: GetServerSideProps = async ctx => {
    const { query, req, res, locale } = ctx;
    setCanonicalLink(req, res);

    const client = initApolloClient(locale as Locale);

    const { data } = await client.query<ActivityDetailQuery, ActivityDetailQueryVariables>({
        query: ActivityDetailDocument,
        variables: {
            slug: query.slug as string,
        },
    });
    if (!data?.activity) {
        return {
            notFound: true,
        };
    }

    return addApolloState(client, { props: {} });
};
